import { useQuery } from "@tanstack/react-query";
import { ArrowRight, CalendarDays, Hash, MapPin, Phone, Users } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { api } from "../api/client";
import { ErrorState, LoadingState, StatusBadge } from "../components/AsyncState";
import { PageHeader } from "../components/PageHeader";
import { formatDate, formatNumber } from "../lib/format";

type HouseholdDetails = {
  id: string;
  householdCode: string;
  headName: string;
  unitName: string;
  memberCount: number | null;
  status: string;
  notes: string | null;
  lastDeliveredAt: string | null;
  deliveryCount: number;
  foundationalRank: number | null;
  phone: string;
  nationalId: string;
  version: number;
};

export function HouseholdDetailPage() {
  const { id = "" } = useParams();
  const query = useQuery({
    queryKey: ["household", id],
    queryFn: async () => (await api<HouseholdDetails>(`/households/${id}`)).data,
    enabled: Boolean(id)
  });
  if (query.isPending) return <LoadingState />;
  if (query.isError) return <ErrorState error={query.error} retry={() => query.refetch()} />;
  const row = query.data;
  return (
    <div className="page">
      <Link className="back-link" to="/households">
        <ArrowRight aria-hidden="true" />
        العودة إلى الأسر
      </Link>
      <PageHeader
        title={row.headName}
        description={row.householdCode}
        actions={<StatusBadge status={row.status} />}
      />
      <section className="detail-grid">
        <article className="content-card detail-card">
          <h2>بيانات الأسرة</h2>
          <dl className="definition-grid">
            <div>
              <dt>
                <Hash aria-hidden="true" />
                رقم الأسرة
              </dt>
              <dd>{row.householdCode}</dd>
            </div>
            <div>
              <dt>
                <MapPin aria-hidden="true" />
                الوحدة
              </dt>
              <dd>{row.unitName}</dd>
            </div>
            <div>
              <dt>
                <Users aria-hidden="true" />
                عدد الأفراد
              </dt>
              <dd>{formatNumber(row.memberCount)}</dd>
            </div>
            <div>
              <dt>
                <Phone aria-hidden="true" />
                الهاتف
              </dt>
              <dd dir="ltr">{row.phone}</dd>
            </div>
            <div>
              <dt>رقم الهوية</dt>
              <dd dir="ltr">{row.nationalId}</dd>
            </div>
            <div>
              <dt>ترتيب الدورة التأسيسية</dt>
              <dd>{row.foundationalRank ? formatNumber(row.foundationalRank) : "لم يُحدّد"}</dd>
            </div>
          </dl>
        </article>
        <article className="content-card detail-card">
          <h2>سجل الاستحقاق</h2>
          <div className="highlight-stat">
            <CalendarDays aria-hidden="true" />
            <div>
              <span>آخر استلام</span>
              <strong>{formatDate(row.lastDeliveredAt)}</strong>
            </div>
          </div>
          <div className="highlight-stat">
            <Users aria-hidden="true" />
            <div>
              <span>إجمالي مرات الاستلام</span>
              <strong>{formatNumber(row.deliveryCount)}</strong>
            </div>
          </div>
          {row.notes ? (
            <div className="note-box">
              <strong>ملاحظات</strong>
              <p>{row.notes}</p>
            </div>
          ) : null}
        </article>
      </section>
    </div>
  );
}
